import { useEffect, useState } from "react";
import { CalendarDays, CircleUserRound, Home, LibraryBig, Sun } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { api, routes } from "@/lib/ppcp";
import { SHELL } from "@/constants/testIds";

/** The plan the reader is working through. Profile is shared by every plan, so it borrows this to
 *  keep the reader's place: open it from inside a plan and the plan's tabs stay, open it from the
 *  library and they are gone. Cleared at the library, which is where a reader leaves a plan. */
const PLAN_CONTEXT = "ppcp:plan-context";

/** "Today" must land on the day the reader is actually on in THIS plan, from anywhere in the app —
 *  including Profile, which has no plan of its own. Resolved once per plan and remembered, so the
 *  tab bar never waits on a request. */
const cachedToday = {};

const resolveToday = async (slug) => {
  const { data } = await api.get("/programs");
  const plan = data.find((p) => p.slug === slug);
  const day = plan?.progress?.finished ? 1 : (plan?.progress?.resume_day ?? 1);
  return routes.day(slug, day);
};

/** The bar follows where the reader is: outside a plan only the two places that exist there, and
 *  inside a plan the whole plan — the same five items for every program, present and current, so a
 *  sub-page never takes a destination away. */
export const BottomNav = ({ slug, todayHref }) => {
  const { pathname } = useLocation();
  const remembered = pathname.includes("/profile") ? sessionStorage.getItem(PLAN_CONTEXT) : null;
  const planSlug = slug || remembered || null;
  const [today, setToday] = useState(null);
  const inPlan = !!planSlug;

  useEffect(() => {
    if (slug) sessionStorage.setItem(PLAN_CONTEXT, slug);
    else if (pathname.includes("/library")) sessionStorage.removeItem(PLAN_CONTEXT);
  }, [slug, pathname]);

  useEffect(() => {
    if (!planSlug || todayHref) return;
    if (cachedToday[planSlug]) {
      setToday(cachedToday[planSlug]);
      return;
    }
    let alive = true;
    resolveToday(planSlug)
      .then((href) => {
        cachedToday[planSlug] = href;
        if (alive) setToday(href);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [todayHref, planSlug]);

  const library = { to: routes.library, label: "My Plans", Icon: LibraryBig, testId: SHELL.navLibrary };
  const profile = { to: routes.profile, label: "Profile", Icon: CircleUserRound, testId: SHELL.navProfile };

  const links = inPlan
    ? [
        library,
        { to: routes.program(planSlug), label: "Overview", Icon: Home, testId: SHELL.navProgram, end: true },
        { to: routes.days(planSlug), label: "Day Index", Icon: CalendarDays, testId: SHELL.navDays },
        {
          to: todayHref ?? today ?? routes.day(planSlug, 1),
          label: "Today",
          Icon: Sun,
          testId: SHELL.navToday,
          // Any day page is "Today" as far as the bar is concerned.
          isActive: (pathname) => pathname.includes(`/p/${planSlug}/day/`),
        },
        profile,
      ]
    : [library, profile];

  return (
    <nav
      data-testid={SHELL.bottomNav}
      className="sticky bottom-0 z-30 border-t border-border bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_28px_rgba(23,64,59,0.06)]"
    >
      <div className="mx-auto flex w-full max-w-shell items-stretch justify-around gap-0.5 px-1.5 py-1 sm:gap-1 sm:px-8">
        {links.map(({ to, label, Icon, testId, end, isActive: matches }) => (
          <NavLink
            key={label}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              cn(
                "ps-tap flex min-h-[46px] min-w-0 flex-1 flex-col items-center justify-center gap-[3px] rounded-full px-0.5 text-[10.5px] font-semibold leading-none transition-colors",
                isActive || matches?.(window.location.pathname)
                  ? "bg-brand-peach text-brand-orange"
                  : "text-brand-tealSoft hover:text-brand-teal"
              )
            }
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
            <span className="max-w-full truncate">{label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
};
